import { Fragment, useRef, useState } from 'react';

import { css, keyframes, styled } from '@mui/material';
import {
  Badge,
  InfoButton,
  LineProgress,
  lineProgressClasses,
  Link,
  Typography,
} from '@kc-console/common-ui';
import { isCompletedTaskStatus, TaskStatusType, useScopedLinkBuilder } from '@kc-console/context';
import { formatValueOrDefault, getDateStringWithFormat, useTranslation } from '@kc-console/core';
import { EllipsisText } from '@kc-console/shared-ui';
import {
  IcChevronDownSmallRegular,
  IcChevronRightSmallRegular,
  IcChevronUpSmallRegular,
  IcCloseSmallRegular,
} from '@kep/kcds-icons';

import { getProgressTone } from './constants';
import TaskStatusIcon from './TaskStatusIcon';

import type { AnimationEvent } from 'react';
import type { Task } from '@kc-console/context';

interface TaskItemCardProps {
  onDismiss: (taskId: string) => void;
  task: Task;
  entering?: boolean;
  leaving?: boolean;
  onLeaveEnd?: (taskId: string) => void;
}

const ROW_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const ENTER_DURATION = '0.32s';
const EXIT_DURATION = '0.24s';

const slideIn = keyframes`
  from {
    grid-template-rows: 0fr;
    opacity: 0;
  }
  to {
    grid-template-rows: 1fr;
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    grid-template-rows: 1fr;
    opacity: 1;
  }
  to {
    grid-template-rows: 0fr;
    opacity: 0;
  }
`;

interface StepStatusIconProps {
  statusType: string;
}

/**
 * step 상태 아이콘. 상태만 표기하며, 실패 사유는 제목 뒤 `StepErrorInfo`(빨간 error(!) 버튼) 툴팁으로 노출한다.
 */
const StepStatusIcon = ({ statusType }: StepStatusIconProps) => (
  <TaskStatusIcon statusType={statusType} size="sm" />
);

/**
 * 실패 step의 오류 사유 안내(빨간 error(!) 버튼 + hover 툴팁).
 * @description
 * - 벌크 작업의 개별 실패 step에서 제목 뒤에 노출한다. error.message가 있을 때만 렌더한다.
 */
const StepErrorInfo = ({ message }: { message: string }) => (
  <InfoButton
    alertType="tooltip"
    arrow="down"
    size="sm"
    color="red"
    icon="error"
    tooltipProps={{ message }}
  />
);

/**
 * 태스크 매니저 카드 1건.
 * @description
 * - 상태 아이콘 + 서비스 칩(Badge) + 시각 + 제목(+링크) + 진행바 + 오류 + 상세(steps) 구성.
 * - 상세 보기 펼침/접힘은 FE 로컬 상태로 관리한다.
 * - 완료(SUCCESS/FAILED) 건에만 단건 제거(X) 버튼을 노출한다.
 */
const TaskItemCard = ({
  task,
  onDismiss,
  entering = false,
  leaving = false,
  onLeaveEnd,
}: TaskItemCardProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // 마운트 시점의 entering 값을 한 번만 붙잡아 둔다 — 이후 prop이 바뀌어도 enter 애니메이션이 끊기지 않도록.
  const enterOnce = useRef(entering).current;
  // 콘솔 내 화면 이동 props 팩토리 — 같은 스코프면 router.push(SPA), 다른 스코프면 full reload.
  const { getProps } = useScopedLinkBuilder();

  // exit(slideOut) 애니메이션이 끝나면 리스트에 알려 실제로 제거한다.
  const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (leaving && event.target === event.currentTarget) {
      onLeaveEnd?.(task.id);
    }
  };

  const tone = getProgressTone(task.statusType);
  const resourceLabel = task.serviceName;
  const title = task.title;

  const steps = task.steps ?? [];
  const hasSteps = steps.length > 0;
  // 완전 실패(FAILED)뿐 아니라 부분 실패(WARNING)도 오류 메시지를 노출한다.
  // 탭 그룹핑(matchesTaskTab)과 동일하게 두 상태를 '실패'로 함께 취급한다.
  const isFailedGroup =
    task.statusType === TaskStatusType.FAILED || task.statusType === TaskStatusType.WARNING;
  const completed = isCompletedTaskStatus(task.statusType);
  // 부분 실패(WARNING)는 진행바 톤과 동일하게 오류 메시지도 warning 색으로,
  // 완전 실패(FAILED)는 danger 색으로 노출한다.
  const errorTextColor =
    task.statusType === TaskStatusType.WARNING ? 'text/warning/default' : 'text/danger/default';

  // 서브태스크(상세 보기)가 있는 경우에만 진행바 옆에 (완료/전체) 갯수를 함께 표기.
  const percentLabel = hasSteps
    ? `${task.progressPercent}% (${task.completedCount}/${task.totalCount})`
    : `${task.progressPercent}%`;

  return (
    <Root entering={enterOnce} leaving={leaving} onAnimationEnd={handleAnimationEnd}>
      <Clip>
        <Inner className="task-card-inner">
          <MainRow>
            <TaskStatusIcon statusType={task.statusType} size="lg" />

            <Body>
              <MetaRow>
                {resourceLabel && (
                  <Badge color="blue" variant="tint" shape="rectangular" label={resourceLabel} />
                )}
                {task.startedAt && (
                  <Typography variant="label/regular/2xs" color="text/weakest/default">
                    {getDateStringWithFormat(task.startedAt, 'DATE_TIME_WITH_DAY_NO_SECONDS')}
                  </Typography>
                )}
                {completed && (
                  <DismissButton
                    type="button"
                    className="task-dismiss"
                    onClick={() => onDismiss(task.id)}
                    aria-label={t('layout:task_manager.item.remove.label')}
                  >
                    <IcCloseSmallRegular width={16} height={16} />
                  </DismissButton>
                )}
              </MetaRow>

              <TitleRow>
                {task.link ? (
                  <TitleLink {...getProps(task.link)}>
                    <EllipsisText variant="label/medium/md">{title}</EllipsisText>
                    <IcChevronRightSmallRegular width={16} height={16} />
                  </TitleLink>
                ) : (
                  <EllipsisText variant="label/medium/md">{title}</EllipsisText>
                )}
              </TitleRow>

              <ProgressRow>
                <ProgressWrap tone={tone} isInProgress={!completed}>
                  <LineProgress value={task.progressPercent} />
                </ProgressWrap>
                <Typography variant="label/regular/sm" color="text/weakest/default">
                  {percentLabel}
                </Typography>
              </ProgressRow>

              {isFailedGroup && task.error?.message && (
                <ErrorText variant="label/regular/sm" color={errorTextColor}>
                  {task.error.message}
                </ErrorText>
              )}

              {hasSteps && (
                <ToggleButton
                  type="button"
                  onClick={() => setExpanded(prev => !prev)}
                  aria-expanded={expanded}
                >
                  <Typography variant="label/medium/xs" color="text/weakest/default">
                    {t('layout:task_manager.item.detail.label')}
                  </Typography>
                  {expanded ? (
                    <IcChevronUpSmallRegular width={16} height={16} />
                  ) : (
                    <IcChevronDownSmallRegular width={16} height={16} />
                  )}
                </ToggleButton>
              )}

              {hasSteps && expanded && (
                <StepBox>
                  {steps.map(step => (
                    <Fragment key={step.stepKey}>
                      <StepRow>
                        <StepStatusIcon statusType={step.statusType} />
                        <StepTitleGroup>
                          <StepName variant="label/regular/xs" color="text/default">
                            {formatValueOrDefault({ origin: step.title })}
                          </StepName>
                          {step.error?.message && <StepErrorInfo message={step.error.message} />}
                        </StepTitleGroup>
                        {step.progressPercent != null && (
                          <StepPercent variant="label/regular/xs" color="text/weakest/default">
                            {step.progressPercent}%
                          </StepPercent>
                        )}
                      </StepRow>

                      {/* 손자 step(3-depth): └ 들여쓰기 + 이름 + progressPercent (상태 아이콘·오류 info는 미노출). */}
                      {(step.steps ?? []).map(child => (
                        <SubStepRow key={child.stepKey}>
                          <SubStepMarker>└</SubStepMarker>
                          <StepName variant="label/regular/xs" color="text/weakest/default">
                            {formatValueOrDefault({ origin: child.title })}
                          </StepName>
                          {child.progressPercent != null && (
                            <StepPercent variant="label/regular/xs" color="text/weakest/default">
                              {child.progressPercent}%
                            </StepPercent>
                          )}
                        </SubStepRow>
                      ))}
                    </Fragment>
                  ))}
                </StepBox>
              )}
            </Body>
          </MainRow>
        </Inner>
      </Clip>
    </Root>
  );
};

export default TaskItemCard;

const Root = styled('div', {
  shouldForwardProp: prop => prop !== 'entering' && prop !== 'leaving',
})<{ entering: boolean; leaving: boolean }>(
  ({ entering, leaving }) => css`
    display: grid;
    grid-template-rows: 1fr;

    /* enter: 데이터에 새로 생겨 DOM 삽입될 때 슬라이드인. exit: 데이터에서 사라질 때 슬라이드아웃. */
    ${entering &&
    !leaving &&
    css`
      animation: ${slideIn} ${ENTER_DURATION} ${ROW_EASING} both;
    `}

    ${leaving &&
    css`
      animation: ${slideOut} ${EXIT_DURATION} ${ROW_EASING} both;
    `}

    &:first-of-type .task-card-inner {
      border-top: none;
    }

    /* 항목 Hover/Select 배경 효과는 없음 — hover 시 제거(X) 버튼만 노출. */
    &:hover .task-dismiss,
    &:focus-within .task-dismiss {
      opacity: 1;
      pointer-events: auto;
    }
  `,
);

// grid-template-rows 0fr일 때 내용을 잘라내는 박스. border는 안쪽 Inner에 둬야
// 같이 잘린다(Clip에 두면 테두리는 안 잘려 접힘 끝에 1px이 남아 끊겨 보임).
const Clip = styled('div')`
  min-height: 0;
  overflow: hidden;
`;

const Inner = styled('div')(
  ({ theme }) => css`
    /* 카드 외부 패딩(위아래 10px). 내부 MainRow가 8px을 한 번 더 가진다. */
    padding: 10px 0;
    border-top: ${theme.mixins.border('border/weaker/default')};
  `,
);

const MainRow = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 8px 0;
`;

const Body = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 6px;
`;

const MetaRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Ghost Icon Button(Secondary, Xs/20px) — DS IconButtonGhost는 최소 sm(28px)이라 20px이 없어 동일 스펙으로 구현.
const DismissButton = styled('button')(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-left: auto;
    width: 20px;
    height: 20px;
    padding: 0;
    background: none;
    border: none;
    border-radius: 1px;
    cursor: pointer;
    color: ${theme.color['icon/weak/default']};
    /* 기본 숨김 — 카드 hover/focus 시에만 노출(Root에서 제어). */
    opacity: 0;
    pointer-events: none;
    transition:
      opacity 0.15s ease,
      background-color 0.15s ease;

    &:hover {
      background-color: ${theme.color['actionSurface/ghost/hover']};
    }

    &:focus-visible {
      outline: 2px solid ${theme.color['border/select']};
      outline-offset: 2px;
    }
  `,
);

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

// DS Link(common-ui) 기반. 이동은 useScopedLinkBuilder().getProps()가 담당한다
// (같은 스코프 → router.push(SPA), 다른 스코프 → full reload, ctrl/cmd+click 새 탭 보존).
// Link 기본색은 blue지만 카드 제목은 기존 제목색(text/default)을 유지하므로 색상만 override 한다.
const TitleLink = styled(Link)(
  ({ theme }) => css`
    && {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
      color: ${theme.color['text/default']};
      text-decoration: none;
    }

    &&:hover {
      color: ${theme.color['text/default']};
      text-decoration: underline;
    }
  `,
);

const ProgressRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 진행 중 fill 위를 흐르는 shimmer(sweep) 애니메이션.
const progressSweep = keyframes`
  0% {
    transform: translateX(-160%);
  }
  100% {
    transform: translateX(190%);
  }
`;

const ProgressWrap = styled('div', {
  shouldForwardProp: prop => prop !== 'tone' && prop !== 'isInProgress',
})<{
  isInProgress: boolean;
  tone: 'default' | 'success' | 'failed' | 'warning';
}>(
  ({ theme, tone, isInProgress }) => css`
    flex: 1;
    min-width: 0;

    ${tone === 'success' &&
    css`
      && .${lineProgressClasses.line} > span {
        background-color: ${theme.color['surface/success/default']};
      }
    `}
    ${tone === 'failed' &&
    css`
      && .${lineProgressClasses.line} > span {
        background-color: ${theme.color['surface/danger/default']};
      }
    `}
    ${tone === 'warning' &&
    css`
      && .${lineProgressClasses.line} > span {
        background-color: ${theme.color['surface/warning/default']};
      }
    `}

    ${isInProgress &&
    css`
      && .${lineProgressClasses.line} > span {
        /* MUI bar(span)는 이미 position:absolute라 그 값을 유지한다.
           relative로 덮으면 top/bottom:0이 무효화돼 fill 높이가 0으로 붕괴한다. */
        overflow: hidden;
      }

      && .${lineProgressClasses.line} > span::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 55%;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.5) 50%,
          transparent 100%
        );
        animation: ${progressSweep} 1.8s ease-in-out infinite;
      }

      @media (prefers-reduced-motion: reduce) {
        && .${lineProgressClasses.line} > span::after {
          animation: none;
        }
      }
    `}
  `,
);

// 오류 메시지는 위 진행바와 0px로 붙인다 — Body의 6px gap을 음수 마진으로 상쇄.
const ErrorText = styled(Typography)`
  margin-top: -6px;
`;

const ToggleButton = styled('button')(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    align-self: flex-start;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: ${theme.color['text/weakest/default']};

    &:focus-visible {
      outline: 2px solid ${theme.color['border/select']};
      outline-offset: 2px;
      border-radius: 2px;
    }
  `,
);

const StepBox = styled('div')(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 12px;
    border: ${theme.mixins.border('border/weaker/default')};
    border-radius: 4px;
    background-color: ${theme.color['surface/neutral/weakest/default']};
    max-height: 160px;
    overflow-y: auto;
  `,
);

const StepRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
`;

// 손자 step 행 — 부모 대비 가볍게 들여쓰기.
const SubStepRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-left: 17px;
`;

// └ 마커 — 손자 이름(text/weakest/default)과 같은 색으로 맞춘다.
const SubStepMarker = styled('span')(
  ({ theme }) => css`
    flex-shrink: 0;
    line-height: 1;
    color: ${theme.color['text/weakest/default']};
  `,
);

// 제목 + 오류 info 버튼 묶음. flex:1로 남은 폭을 차지하되, 내부에서 제목이 info에 붙어 나온다.
const StepTitleGroup = styled('span')`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

// step / 손자 step 이름. 말줄임과 "잘렸을 때만 뜨는" 전체 텍스트 툴팁을 EllipsisText가 함께 담당한다
// (CSS text-overflow만 쓰면 말줄임은 되지만 툴팁이 붙지 않는다). 툴팁 메시지는 children이 그대로 쓰인다.
const StepName = styled(EllipsisText)`
  flex: 0 1 auto;
  min-width: 0;
`;

// step / 손자 step 우측 진행률(%). progressPercent가 있을 때만 노출. margin-left:auto로 우측 정렬.
const StepPercent = styled(Typography)`
  flex-shrink: 0;
  margin-left: auto;
  white-space: nowrap;
`;
